import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import MainAccountCard from './MainAccountCard';
import MainAccountRecords from './MainAccountRecords';
import SubAccountList from './SubAccountList';
import SubAccountDetail from './SubAccountDetail';
import CourtUsageStatistics from './CourtUsageStatistics';
import './CourtUsageHome.css';

/**
 * 球场使用情况管理主页面
 * 
 * @returns {JSX.Element} 主页面组件
 */
const CourtUsageHome = () => {
  const navigate = useNavigate();
  const [courts, setCourts] = useState([]);
  const [selectedCourtId, setSelectedCourtId] = useState('');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview'、'records'、'subAccounts' 或 'statistics'
  const [selectedSubAccountId, setSelectedSubAccountId] = useState(null); // 选中的子账户ID
  const [showCreateForm, setShowCreateForm] = useState(false); // 显示创建场地表单
  const subAccountListRef = useRef(null); // 子账户列表组件的引用
  const [formData, setFormData] = useState({
    name: '',
    rechargeRate: '',
    description: '',
    isActive: true,
    order: 0
  });

  const baseUrl = getApiBaseUrl();

  // 获取场地列表
  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/court-usage/courts?isActive=true`);
      if (response.data.success) {
        const courtsList = response.data.data || [];
        setCourts(courtsList);
        if (courtsList.length > 0 && !selectedCourtId) {
          setSelectedCourtId(courtsList[0]._id);
        }
      }
    } catch (err) {
      console.error('获取场地列表失败:', err);
      setError(err.response?.data?.message || err.message || '获取场地列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取主账户概览
  const fetchOverview = async (courtId) => {
    if (!courtId) return;
    
    try {
      const response = await axios.get(`${baseUrl}/api/court-usage/main-account/overview`, {
        params: { courtId }
      });
      if (response.data.success) {
        setOverview(response.data.data);
      } else {
        throw new Error(response.data.message || '获取概览失败');
      }
    } catch (err) {
      console.error('获取概览失败:', err);
      setError(err.response?.data?.message || err.message || '获取概览失败');
    }
  };

  useEffect(() => {
    fetchCourts();
  }, []);

  useEffect(() => {
    if (selectedCourtId && activeTab === 'overview') {
      const loadData = async () => {
        setLoading(true);
        setError(null);
        await fetchOverview(selectedCourtId);
        setLoading(false);
      };
      loadData();
    } else if (!selectedCourtId) {
      // 如果没有选中场地，确保 loading 状态正确
      setLoading(false);
    }
  }, [selectedCourtId, activeTab]);

  const handleBack = () => {
    navigate('/');
  };

  const handleRefresh = async () => {
    if (selectedCourtId && activeTab === 'overview') {
      setLoading(true);
      setError(null);
      await fetchOverview(selectedCourtId);
      setLoading(false);
    }
    // 如果当前在子账户标签页，也需要刷新子账户列表
    // 子账户列表的刷新由 SubAccountList 组件内部处理
  };

  const handleCourtChange = (courtId) => {
    setSelectedCourtId(courtId);
    setOverview(null);
  };

  // 打开创建场地表单
  const handleOpenCreateForm = () => {
    setFormData({
      name: '',
      rechargeRate: '',
      description: '',
      isActive: true,
      order: 0
    });
    setShowCreateForm(true);
    setError(null);
  };

  // 关闭创建场地表单
  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setFormData({
      name: '',
      rechargeRate: '',
      description: '',
      isActive: true,
      order: 0
    });
  };

  // 提交创建场地表单
  const handleCreateCourt = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('场地名称不能为空');
      return;
    }
    
    if (!formData.rechargeRate || parseFloat(formData.rechargeRate) <= 0) {
      setError('充值换算率必须大于0');
      return;
    }

    try {
      setError(null);
      const response = await axios.post(`${baseUrl}/api/court-usage/courts`, {
        name: formData.name.trim(),
        rechargeRate: parseFloat(formData.rechargeRate),
        description: formData.description.trim(),
        isActive: formData.isActive,
        order: parseInt(formData.order) || 0
      });

      if (response.data.success) {
        await fetchCourts();
        handleCloseCreateForm();
        // 如果创建成功，自动选中新创建的场地
        if (response.data.data && response.data.data._id) {
          setSelectedCourtId(response.data.data._id);
        }
      } else {
        throw new Error(response.data.message || '创建场地失败');
      }
    } catch (err) {
      console.error('创建场地失败:', err);
      setError(err.response?.data?.message || err.message || '创建场地失败');
    }
  };

  // 只在初始加载场地列表时显示加载状态
  if (loading && courts.length === 0 && !error) {
    return (
      <div className="court-usage-home">
        <div className="court-usage-header">
          <button className="back-button" onClick={handleBack}>← 返回首页</button>
          <h1 className="court-usage-title">球场使用管理</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="court-usage-home">
      <div className="court-usage-header">
        <button className="back-button" onClick={handleBack}>← 返回首页</button>
        <h1 className="court-usage-title">球场使用管理</h1>
        <button className="refresh-button" onClick={handleRefresh} title="刷新">
          🔄
        </button>
      </div>

      {/* 场地选择 */}
      <div className="court-selector">
        {courts.length > 0 ? (
          <>
            <label>选择场地</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select
                value={selectedCourtId}
                onChange={(e) => handleCourtChange(e.target.value)}
                className="court-select"
                style={{ flex: 1 }}
              >
                {courts.map(court => (
                  <option key={court._id} value={court._id}>
                    {court.name} ({court.rechargeRate}元/场时)
                  </option>
                ))}
              </select>
              <button 
                className="add-court-button" 
                onClick={handleOpenCreateForm}
                title="添加场地"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4361ee',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                + 添加场地
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>暂无可用场地</span>
            <button 
              className="add-court-button" 
              onClick={handleOpenCreateForm}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4361ee',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              + 添加场地
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {!selectedCourtId && courts.length === 0 && activeTab !== 'subAccounts' && (
        <div className="empty-state">
          <p>暂无可用场地，请先创建场地</p>
          <button className="primary-button" onClick={() => navigate('/court-usage')}>
            去创建场地
          </button>
        </div>
      )}

      {/* 标签页切换 */}
      <div className="tab-container">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          disabled={!selectedCourtId}
        >
          概览
        </button>
        <button
          className={`tab-button ${activeTab === 'records' ? 'active' : ''}`}
          onClick={() => setActiveTab('records')}
          disabled={!selectedCourtId}
        >
          记录
        </button>
        <button
          className={`tab-button ${activeTab === 'subAccounts' ? 'active' : ''}`}
          onClick={() => setActiveTab('subAccounts')}
        >
          子账户
        </button>
        <button
          className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          统计
        </button>
      </div>

      {/* 内容区域 */}
      <div className="court-usage-content">
        {activeTab === 'overview' && selectedCourtId && (
          <MainAccountCard
            courtId={selectedCourtId}
            court={courts.find(c => c._id === selectedCourtId)}
            overview={overview}
            onRefresh={handleRefresh}
          />
        )}
        
        {activeTab === 'records' && selectedCourtId && (
          <MainAccountRecords
            courtId={selectedCourtId}
            onRefresh={handleRefresh}
          />
        )}

        {activeTab === 'subAccounts' && !selectedSubAccountId && (
          selectedCourtId ? (
            <SubAccountList
              ref={subAccountListRef}
              courtId={selectedCourtId}
              onRefresh={handleRefresh}
              onAccountClick={(account) => {
                setSelectedSubAccountId(account._id);
              }}
            />
          ) : (
            <div className="empty-state">
              <p>请先选择场地</p>
            </div>
          )
        )}

        {activeTab === 'subAccounts' && selectedSubAccountId && (
          <SubAccountDetail
            accountId={selectedSubAccountId}
            onBack={() => setSelectedSubAccountId(null)}
            onRefresh={handleRefresh}
          />
        )}

        {activeTab === 'statistics' && (
          <CourtUsageStatistics onRefresh={handleRefresh} />
        )}

        {activeTab === 'overview' && !selectedCourtId && (
          <div className="empty-state">
            <p>请先选择场地</p>
          </div>
        )}

        {activeTab === 'records' && !selectedCourtId && (
          <div className="empty-state">
            <p>请先选择场地</p>
          </div>
        )}
      </div>

      {/* 创建场地表单模态框 */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={handleCloseCreateForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>添加场地</h2>
              <button className="close-button" onClick={handleCloseCreateForm}>×</button>
            </div>
            <form onSubmit={handleCreateCourt} className="court-form">
              {error && (
                <div className="error-message" style={{ marginBottom: '15px' }}>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="court-name">场地名称 *</label>
                <input
                  type="text"
                  id="court-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入场地名称"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="recharge-rate">充值换算率（元/场时） *</label>
                <input
                  type="number"
                  id="recharge-rate"
                  value={formData.rechargeRate}
                  onChange={(e) => setFormData({ ...formData, rechargeRate: e.target.value })}
                  placeholder="例如：130（表示130元=1场时）"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="court-description">场地说明</label>
                <textarea
                  id="court-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="可选，填写场地说明"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  启用此场地
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCloseCreateForm}>
                  取消
                </button>
                <button type="submit" className="submit-button">
                  创建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtUsageHome;

